/**
 * @fileoverview Types for Mintsoft integration
 *
 * Including:
 * - Types for Mintsoft webhook topics
 * - Types corresponding to rjsf schemas for Mintsoft integration action steps inputs and results
 * - Types for request body sent to Mintsoft API
 * - Types for Mintsoft API response data
 *
 * Organized by entities:
 * - Inventory
 * - Product
 * - Order
 */

import { z } from 'zod'
import { SupportedMintsoftWebhookTopics } from '../action-auto-trigger.js'

export const MintSoftWebhookDisplayNames: Record<SupportedMintsoftWebhookTopics, string> = {
  [SupportedMintsoftWebhookTopics.ORDER_DISPATCHED]: 'Order Dispatched',
  [SupportedMintsoftWebhookTopics.ORDER_CANCELLED]: 'Order Cancelled',
  [SupportedMintsoftWebhookTopics.ORDER_DELIVERED]: 'Order Delivered',
  [SupportedMintsoftWebhookTopics.ORDER_SPLIT]: 'Order Split',
}

/**
 * Schema for Mintsoft Order object
 */
export const mintsoftOrderSchema = z.object({
  Channel: z
    .object({
      Name: z.string().optional().nullable(),
      Description: z.string().optional().nullable(),
      Active: z.boolean().optional().nullable(),
      Logo: z.string().optional().nullable(),
      ClientId: z.number().optional().nullable(),
      ID: z.number().optional().nullable(),
      LastUpdated: z.string().optional().nullable(),
      LastUpdatedByUser: z.string().optional().nullable(),
    })
    .optional()
    .nullable(),

  Country: z
    .object({
      Name: z.string().optional().nullable(),
      Code: z.string().optional().nullable(),
      Code3: z.string().optional().nullable(),
      ID: z.number().optional().nullable(),
      LastUpdated: z.string().optional().nullable(),
      LastUpdatedByUser: z.string().optional().nullable(),
    })
    .optional()
    .nullable(),

  Currency: z
    .object({
      Name: z.string().optional().nullable(),
      Code: z.string().optional().nullable(),
      Symbol: z.string().optional().nullable(),
      ID: z.number().optional().nullable(),
      LastUpdated: z.string().optional().nullable(),
      LastUpdatedByUser: z.string().optional().nullable(),
    })
    .optional()
    .nullable(),

  OrderNameValues: z
    .array(
      z.object({
        OrderId: z.number().optional().nullable(),
        Name: z.string().optional().nullable(),
        Value: z.string().optional().nullable(),
        ID: z.number().optional().nullable(),
        LastUpdated: z.string().optional().nullable(),
        LastUpdatedByUser: z.string().optional().nullable(),
      }),
    )
    .optional()
    .nullable(),

  RecipientType: z.string().optional().nullable(),
  ClientId: z.number().optional().nullable(),
  CLIENT_CODE: z.string().optional().nullable(),
  OrderNumber: z.string().optional().nullable(),
  ExternalOrderReference: z.string().optional().nullable(),
  OrderDate: z.string().optional().nullable(),
  DespatchDate: z.string().optional().nullable(),
  RequiredDespatchDate: z.string().optional().nullable(),
  RequiredDeliveryDate: z.string().optional().nullable(),
  Title: z.string().optional().nullable(),
  FirstName: z.string().optional().nullable(),
  LastName: z.string().optional().nullable(),
  CompanyName: z.string().optional().nullable(),
  Address1: z.string().optional().nullable(),
  Address2: z.string().optional().nullable(),
  Address3: z.string().optional().nullable(),
  Town: z.string().optional().nullable(),
  County: z.string().optional().nullable(),
  PostCode: z.string().optional().nullable(),
  Phone: z.string().optional().nullable(),
  Mobile: z.string().optional().nullable(),
  Email: z.string().email().optional().nullable(),
  CountryId: z.number().optional().nullable(),
  Source: z.string().optional().nullable(),
  Comments: z.string().optional().nullable(),
  GiftMessages: z.string().optional().nullable(),
  DeliveryNotes: z.string().optional().nullable(),
  VATNumber: z.string().optional().nullable(),
  EORINumber: z.string().optional().nullable(),
  PIDNumber: z.string().optional().nullable(),
  OrderStatusId: z.number().optional().nullable(),
  NumberOfParcels: z.number().optional().nullable(),
  TotalItems: z.number().optional().nullable(),
  TotalWeight: z.number().optional().nullable(),
  OrderValue: z.number().optional().nullable(),
  Part: z.number().optional().nullable(),
  NumberOfParts: z.number().optional().nullable(),
  CourierServiceTypeId: z.number().optional().nullable(),
  CourierServiceId: z.number().optional().nullable(),
  CourierServiceName: z.string().optional().nullable(),
  TrackingNumber: z.string().optional().nullable(),
  TrackingURL: z.string().optional().nullable(),
  ShippingTotalExVat: z.number().optional().nullable(),
  ShippingTotalVat: z.number().optional().nullable(),
  DiscountTotalExVat: z.number().optional().nullable(),
  DiscountTotalVat: z.number().optional().nullable(),
  TotalVat: z.number().optional().nullable(),
  PIIRemoved: z.boolean().optional().nullable(),
  ShippingNet: z.number().optional().nullable(),
  ShippingTax: z.number().optional().nullable(),
  ShippingGross: z.number().optional().nullable(),
  DiscountNet: z.number().optional().nullable(),
  DiscountTax: z.number().optional().nullable(),
  TotalOrderNet: z.number().optional().nullable(),
  TotalOrderTax: z.number().optional().nullable(),
  TotalOrderGross: z.number().optional().nullable(),
  DiscountGross: z.number().optional().nullable(),
  WarehouseId: z.number().optional().nullable(),
  WAREHOUSE_CODE: z.string().optional().nullable(),
  ChannelId: z.number().optional().nullable(),
  CurrencyId: z.number().optional().nullable(),
  DespatchedByUser: z.string().optional().nullable(),

  OrderItems: z
    .array(
      z.object({
        OrderItemNameValues: z
          .array(
            z.object({
              Name: z.string().optional().nullable(),
              Value: z.string().optional().nullable(),
              Internal: z.boolean().optional().nullable(),
              ID: z.number().optional().nullable(),
              LastUpdated: z.string().optional().nullable(),
              LastUpdatedByUser: z.string().optional().nullable(),
            }),
          )
          .optional()
          .nullable(),
        OrderId: z.number().optional().nullable(),
        ProductId: z.number().optional().nullable(),
        Quantity: z.number().optional().nullable(),
        Allocated: z.number().optional().nullable(),
        Commited: z.number().optional().nullable(),
        OnBackOrder: z.number().optional().nullable(),
        SourceLineSubTotal: z.number().optional().nullable(),
        SourceLineTotalTax: z.number().optional().nullable(),
        SourceLineTotalDiscount: z.number().optional().nullable(),
        SourceLineTotal: z.number().optional().nullable(),
        Price: z.number().optional().nullable(),
        Vat: z.number().optional().nullable(),
        Discount: z.number().optional().nullable(),
        PriceNet: z.number().optional().nullable(),
        Tax: z.number().optional().nullable(),
        DiscountGross: z.number().optional().nullable(),
        TaxRate: z.number().optional().nullable(),
        DiscountNet: z.number().optional().nullable(),
        DiscountTax: z.number().optional().nullable(),
        NetPaid: z.number().optional().nullable(),
        TaxPaid: z.number().optional().nullable(),
        TotalTax: z.number().optional().nullable(),
        Details: z.string().optional().nullable(),
        SKU: z.string().optional().nullable(),
        ID: z.number().optional().nullable(),
        LastUpdated: z.string().optional().nullable(),
        LastUpdatedByUser: z.string().optional().nullable(),
      }),
    )
    .optional()
    .nullable(),

  OrderLock: z.boolean().optional().nullable(),
  Tags: z.string().optional().nullable(),
  SourceOrderDate: z.string().optional().nullable(),
  ID: z.number().optional().nullable(),
  LastUpdated: z.string().optional().nullable(),
  LastUpdatedByUser: z.string().optional().nullable(),
})

export type MintsoftOrder = z.infer<typeof mintsoftOrderSchema>

/** Schema for Mintsoft Split Order webhook payload */
export const mintsoftOrderSplitWebhookPayloadSchema = z.object({
  Order: mintsoftOrderSchema.optional().nullable(),
  SplitOrder: mintsoftOrderSchema.optional().nullable(),
  OrderId: z.number().optional().nullable(),
  SplitOrderId: z.number().optional().nullable(),
  ID: z.number().optional().nullable(),
  LastUpdated: z.string().optional().nullable(),
  LastUpdatedByUser: z.string().optional().nullable(),
})

export type MintsoftOrderSplitWebhookPayload = z.infer<
  typeof mintsoftOrderSplitWebhookPayloadSchema
>

/** Schema for Mintsoft Order Webhook Subscription Response*/
export const mintsoftOrderWebhookSubscriptionResponseSchema = z.object({
  ID: z.number(),
  Success: z.boolean(),
  Message: z.string().optional().nullable(),
  WarningMessage: z.string().optional().nullable(),
  AllocatedFromReplen: z.boolean().optional().nullable(),
})

export type MintsoftOrderWebhookSubscriptionResponse = z.infer<
  typeof mintsoftOrderWebhookSubscriptionResponseSchema
>
